import { ROOF_CLIP_CONTEXT_TOP_OFFSET } from "building-generation/elements/multiGableRoofClipContext";
import { collectBuildingProps } from "building-geometry/props/collectBuildingProps";
import type { Building } from "building-types/building/buildingTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import {
  foundationFaceCategories,
  foundationOcclusionFaceCategories,
  getRoofPlanFaceCategories,
} from "building-types/materials/faceCategories";
import type { GeneratedRoof, RoofWallFaces } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import { sheetGeometry3DCategories } from "building-types/views/sheetGeometryCategory";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { ViewEntityTable } from "building-types/views/viewEntityTable";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import shallowCompare from "common/utils/shallowCompare";
import { filter3DProp } from "doc/plans/sheetGeometryEffects";
import {
  builtInElevationVisibilitySelectors,
  builtInPerspectiveVisibilitySelectors,
  type SheetGeometrySelectorEffectMap,
} from "doc/plans/SheetGeometrySelectorEffectMap";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { faceCategoryFilterFromSheetGeometryCategories } from "doc/viewTemplateTools";
import type { Face } from "geom/brep";
import { faceByFourPoints, faceNew } from "geom/brep";
import {
  buildBSPNode,
  buildPolygonsFromFaces,
  polygonBSPFlip,
  polygonBSPMoveAlongNormal,
  removeCoplanarLinesBSP,
} from "geom/bsp";
import { clipFaces, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { planeCopy, planeCopyReversed, planeNewXY, planeProjectPolyLine3, polyLine2OnPlane } from "geom/plane";
import { polyLine2Copy, polyLine2SetCW, polyLine2Simplify } from "geom/polyLine2";
import { polyLine3EdgeCount } from "geom/polyLine3";
import { simplifyMesh } from "gfx/simplifyMesh";
import { buildFaceGroupsFromMesh, type FaceGroupBSP } from "lib/house/faceGroupBSP";
import { buildOpeningOccluderFaceGroups } from "lib/house/pdf/openingOcclusionFaces";
import { loadWasm } from "lib/house/plans/assets/loadWasm";
import {
  collectBuildingFacesByEntityId,
  collectBuildingMeshes,
  MeshNamingConvention,
} from "lib/house/plans/collectBuildingFaces";
import { buildRoomVolumePolygons } from "lib/house/plans/orthogonalSections";
import type { BuildingBSPNewOptions } from "lib/house/plans/perspective";
import { buildingBSPNew, collectBuildingViewFaceGroups } from "lib/house/plans/perspective";
import type { LazyPropMesh } from "lib/house/plans/perspectiveTypes";
import type { SceneBSP } from "lib/house/plans/SceneBSP";

interface SceneBSPCacheKey {
  readonly categories: readonly FaceCategory[] | undefined;
  readonly hatchCategories: readonly FaceCategory[] | undefined;
  readonly enableHatchMaterials: boolean;
  readonly removeCoplanarLines: boolean;
  readonly addEntityIds: boolean;
  readonly simplifyMeshes: boolean;
  readonly fillOpeningOccluders: boolean;
  readonly filteredPropIds: string[] | undefined;
  readonly selectedReportingUnitLabelId: string | undefined;
}

type SceneBSPCache = LRUObjectCache<SceneBSPCacheKey, SceneBSP>;

function compareSceneBSPCacheKeys(a: SceneBSPCacheKey, b?: SceneBSPCacheKey): boolean {
  return (
    shallowCompare(a.categories, b?.categories) &&
    shallowCompare(a.hatchCategories, b?.hatchCategories) &&
    a.enableHatchMaterials === b?.enableHatchMaterials &&
    a.removeCoplanarLines === b?.removeCoplanarLines &&
    a.addEntityIds === b?.addEntityIds &&
    a.simplifyMeshes === b?.simplifyMeshes &&
    a.fillOpeningOccluders === b?.fillOpeningOccluders &&
    a.selectedReportingUnitLabelId === b?.selectedReportingUnitLabelId &&
    shallowCompare(a.filteredPropIds, b?.filteredPropIds)
  );
}

type BuildingBSPOptionsForCache = Omit<BuildingBSPNewOptions, "building" | "entityTable"> &
  Required<Pick<BuildingBSPNewOptions, "faceCategoryFilter">>;

export interface CachedBuildingBSPOptions extends BuildingBSPOptionsForCache {
  simplifyMeshes?: boolean;

  /** Seal openings with invisible occluders so elevation occlusion can't see through them. */
  readonly fillOpeningOccluders?: boolean;

  /** @deprecated Hacky way to set some roof rendering specific settings in three.js preview */
  readonly isRoofScene?: boolean;
}

/**
 * `faceCategoryFilterFromSheetGeometryCategories` returns one shared, read-only Set per categories
 * array, so the defaults derived from it are built once here rather than by deleting from it.
 */
function derivedDefaultFaceCategoryFilter(excluded: readonly FaceCategory[]): () => Set<FaceCategory> {
  let derived: Set<FaceCategory> | undefined;

  return () => {
    if (!derived) {
      const all = faceCategoryFilterFromSheetGeometryCategories(sheetGeometry3DCategories);
      derived = new Set([...(all ?? [])].filter((category) => !excluded.includes(category)));
    }

    return derived;
  };
}

const defaultSceneFaceCategoryFilter = derivedDefaultFaceCategoryFilter([FaceCategory.PROP_FOLIAGE]);

const defaultOcclusionFaceCategoryFilter = derivedDefaultFaceCategoryFilter([
  FaceCategory.PROP_FOLIAGE,
  FaceCategory.FLOOR_FRAMING,
  FaceCategory.WALL_FRAMING,
  FaceCategory.GABLE_END_WALL_FRAMING,
]);

export function faceCategoryFilterWithDefaultFallback(faceCategoryFilter?: Set<FaceCategory>): Set<FaceCategory> {
  return faceCategoryFilter ?? defaultSceneFaceCategoryFilter();
}

export function buildingSceneBSPOptions(
  faceCategoryFilter?: Set<FaceCategory>,
  hatchCategoryFilter?: Set<FaceCategory>,
  visibilitySelectors?: SheetGeometrySelectorEffectMap[],
): CachedBuildingBSPOptions {
  return {
    faceCategoryFilter: faceCategoryFilterWithDefaultFallback(faceCategoryFilter),
    hatchCategoryFilter,
    visibilitySelectors,
    enableHatchMaterials: true,
    addEntityIds: true,
    removeCoplanarLines: true,
  };
}

/**
 * Built-in plans have hardcoded label visibility filters for elevation and perspective views.
 * They are used to filter props.
 */
export function builtInPlanBuildingSceneBSPOptions(
  elevation: boolean,
  faceCategoryFilter?: Set<FaceCategory>,
): CachedBuildingBSPOptions {
  return {
    faceCategoryFilter: faceCategoryFilterWithDefaultFallback(faceCategoryFilter),
    visibilitySelectors: elevation ? builtInElevationVisibilitySelectors() : builtInPerspectiveVisibilitySelectors(),
    enableHatchMaterials: true,
    addEntityIds: true,
    removeCoplanarLines: true,
  };
}

export function elevationOcclusionSceneBSPOptions(
  faceCategoryFilter?: Set<FaceCategory>,
  hatchCategoryFilter?: Set<FaceCategory>,
  visibilitySelectors?: SheetGeometrySelectorEffectMap[],
): CachedBuildingBSPOptions {
  return {
    faceCategoryFilter: faceCategoryFilter ?? defaultOcclusionFaceCategoryFilter(),
    hatchCategoryFilter,
    visibilitySelectors,
    enableHatchMaterials: false,
    addEntityIds: true,
    removeCoplanarLines: false,
    simplifyMeshes: true,
    fillOpeningOccluders: true,
  };
}

export function roofPlanSceneBSPOptions(includeRidgeVent: boolean): CachedBuildingBSPOptions {
  return {
    faceCategoryFilter: getRoofPlanFaceCategories(includeRidgeVent),
    enableHatchMaterials: false,
    isRoofScene: true,
  };
}

export function foundationSceneBSPOptions(): CachedBuildingBSPOptions {
  return {
    faceCategoryFilter: new Set<FaceCategory>(foundationFaceCategories),
    enableHatchMaterials: false,
  };
}

export function foundationOcclusionSceneBSPOptions(): CachedBuildingBSPOptions {
  return {
    faceCategoryFilter: new Set<FaceCategory>(foundationOcclusionFaceCategories),
    enableHatchMaterials: true, // TODO(should this be enabled?)
  };
}

/**
 * This class is used to cache and lazily produce the BSPs for plan set generation.
 */
export class PlanSetBSPProvider {
  private readonly sceneBSPCache: SceneBSPCache = new LRUObjectCache<SceneBSPCacheKey, SceneBSP>(
    10,
    compareSceneBSPCacheKeys,
  );

  // NOTE: WASM cache is intended to be unlimited in practice.
  // Disposal is manual since it needs async operations.
  private readonly cachedWasmSceneBSPs = new LRUObjectCache<SceneBSPCacheKey, number>(1000, compareSceneBSPCacheKeys);

  // HLR face groups are plain JS geometry, so a small LRU mirroring sceneBSPCache
  // is enough to avoid recomputing the full traversal per distinct view/selector key.
  private readonly sceneViewFaceGroupsCache = new LRUObjectCache<
    SceneBSPCacheKey,
    { faceGroups: FaceGroupBSP[]; lazyPropMeshes: LazyPropMesh[] }
  >(10, compareSceneBSPCacheKeys);

  // Shared by the wasm-BSP and HLR paths, but a given elevation uses exactly one engine,
  // avoiding cross-engine pollution.
  private readonly entityTableCache: LRUObjectCache<SceneBSPCacheKey, ViewEntityTable> = new LRUObjectCache<
    SceneBSPCacheKey,
    ViewEntityTable
  >(10, compareSceneBSPCacheKeys);

  private cachedRoofVolumeBSP: NodeBSP | undefined;

  constructor(private readonly building: Building) {}

  dispose(): void {
    void (async () => {
      const wasm = await loadWasm();
      const wasmBSPs = this.cachedWasmSceneBSPs.getAllValues();

      for (const bsp of wasmBSPs) {
        wasm.wasm?.disposeBSP(bsp);
      }
    })();
  }

  private cacheKeyFromBuildingBSPOptions(options: CachedBuildingBSPOptions): SceneBSPCacheKey {
    // Visibility selectors is not really amenable to using as a cache key since it includes functions,
    // so we're just computing the list of filtered prop ids.
    // We compute the list always even if there are no selectors, so that we maximize cache hits.
    const { visibilitySelectors } = options;
    let props = collectBuildingProps(this.building, options.faceCategoryFilter);
    if (visibilitySelectors !== undefined) {
      props = props.filter((prop) => filter3DProp({ prop, selectors: visibilitySelectors }));
    }
    const filteredPropIds: string[] = props.map((prop) => prop.id).sort((a, b) => a.localeCompare(b, "en-US"));
    return {
      categories: options.faceCategoryFilter ? [...options.faceCategoryFilter].sort() : undefined,
      hatchCategories: options.hatchCategoryFilter ? [...options.hatchCategoryFilter].sort() : undefined,
      enableHatchMaterials: options.enableHatchMaterials ?? false,
      removeCoplanarLines: options.removeCoplanarLines ?? true,
      addEntityIds: options.addEntityIds ?? false,
      simplifyMeshes: options.simplifyMeshes ?? false,
      fillOpeningOccluders: options.fillOpeningOccluders ?? false,
      selectedReportingUnitLabelId: options.selectedReportingUnitLabelId,
      filteredPropIds,
    };
  }

  getSceneViewEntityTable(options: CachedBuildingBSPOptions): ViewEntityTable | undefined {
    const cacheKey: SceneBSPCacheKey = this.cacheKeyFromBuildingBSPOptions(options);
    return this.entityTableCache.get(cacheKey);
  }

  // NOTE: This JS BSP path does not apply `fillOpeningOccluders` — only the wasm path
  // (getWasmSceneBSP) does. Elevation occlusion runs through wasm (doJS = false in
  // elevationAnnotationSVG.ts), so this is fine today; flipping to the JS path for debugging
  // would silently skip opening occluders.
  async getSceneBSP(options: CachedBuildingBSPOptions): Promise<SceneBSP | undefined> {
    const cacheKey: SceneBSPCacheKey = this.cacheKeyFromBuildingBSPOptions(options);
    if (this.sceneBSPCache.has(cacheKey)) {
      return this.sceneBSPCache.get(cacheKey);
    }

    const entityTable = new ViewEntityTable();
    const bsp = (await buildingBSPNew({ building: this.building, entityTable, ...options })) ?? undefined;
    if (bsp) {
      this.sceneBSPCache.add(cacheKey, bsp);
      this.entityTableCache.add(cacheKey, entityTable);
    }
    return bsp;
  }

  async getWasmSceneBSP(options: CachedBuildingBSPOptions): Promise<number | undefined> {
    const cacheKey: SceneBSPCacheKey = this.cacheKeyFromBuildingBSPOptions(options);
    if (this.cachedWasmSceneBSPs.has(cacheKey)) {
      return this.cachedWasmSceneBSPs.get(cacheKey);
    }
    const wasm = await loadWasm();

    const facesToProcess: FaceGroupBSP[] = [];

    const faceCategoryFilter: Set<FaceCategory> = options.faceCategoryFilter;

    const entityTable = new ViewEntityTable();

    const addEntityIds = options.addEntityIds ?? false;
    const groups = collectBuildingFacesByEntityId({
      building: this.building,
      enableHatchMaterials: false,
      buildMask: faceCategoryFilter,
      entityTable,
      addEntityIds,
    });
    // This is compensating for the wasm BSP not handling props
    let { meshes } = await collectBuildingMeshes({
      building: this.building,
      faceCategoryFilter,
      findMaterials: false,
      visibilitySelectors: options.visibilitySelectors,
      entityTable,
      addEntityIds,
      meshNamingConvention: MeshNamingConvention.UNIQUE_NAMES,
    });

    for (const groupId in groups) {
      const group = groups[groupId];
      facesToProcess.push(...group);
    }

    if (options.simplifyMeshes) {
      meshes = await Promise.all(meshes.map((mesh) => simplifyMesh(mesh)));
    }

    meshes.forEach((mesh) => buildFaceGroupsFromMesh({ mesh, faceGroups: facesToProcess }));

    if (options.fillOpeningOccluders) {
      facesToProcess.push(...buildOpeningOccluderFaceGroups(this.building));
    }

    const newBsp = wasm.wasm?.bspFromFaceGroups(facesToProcess, options.removeCoplanarLines ?? true);

    if (newBsp !== undefined) {
      this.cachedWasmSceneBSPs.add(cacheKey, newBsp);
      this.entityTableCache.add(cacheKey, entityTable);
    }

    return newBsp;
  }

  async getSceneViewFaceGroups(
    options: CachedBuildingBSPOptions,
  ): Promise<{ faceGroups: FaceGroupBSP[]; lazyPropMeshes: LazyPropMesh[] }> {
    const cacheKey: SceneBSPCacheKey = this.cacheKeyFromBuildingBSPOptions(options);
    const cached = this.sceneViewFaceGroupsCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }
    const entityTable = new ViewEntityTable();
    const result = await collectBuildingViewFaceGroups({ building: this.building, entityTable, ...options });
    this.sceneViewFaceGroupsCache.add(cacheKey, result);
    this.entityTableCache.add(cacheKey, entityTable);
    return result;
  }

  getRoofVolumeBSP(): NodeBSP | undefined {
    if (!this.cachedRoofVolumeBSP) {
      this.cachedRoofVolumeBSP = buildRoofVolumeBSP(this.building) ?? undefined;
    }
    return this.cachedRoofVolumeBSP;
  }
}

function buildRoofVolumeFaces(
  subRoof: GeneratedRoof,
  clipContext: MultiGableRoofClipContext,
  roofVolumeFaces: Face[] = [],
): Face[] {
  const { roofVolume, roofTopFaces } = subRoof;
  const { baseHeight } = roofVolume;
  const { discardBSP } = clipContext;

  const bottomPlane = planeNewXY(0, 0, baseHeight + 2 * ROOF_CLIP_CONTEXT_TOP_OFFSET);
  const bottomPlaneReversed = planeCopyReversed(bottomPlane);

  for (const { face } of roofTopFaces) {
    const { loops, plane } = face;

    if (!loops[0] || !plane || !bottomPlane) {
      continue;
    }

    const topLoop = polyLine2Simplify(polyLine2Copy(loops[0]));
    const topLoop3 = polyLine2OnPlane(plane, topLoop);

    const bottomLoop = planeProjectPolyLine3(bottomPlane, topLoop3);
    const bottomLoop3 = polyLine2OnPlane(bottomPlane, bottomLoop);

    const loopCount = topLoop3 ? polyLine3EdgeCount(topLoop3) : 0;

    if (!(topLoop3?.length === bottomLoop3?.length)) {
      console.assert(false);
    }

    const perimeterFaces: Face[] = [];
    for (let i = 0; i < loopCount; i++) {
      const i3 = i * 3;

      const x0 = bottomLoop3[i3 + 3];
      const y0 = bottomLoop3[i3 + 4];
      const z0 = bottomLoop3[i3 + 5];

      const x1 = bottomLoop3[i3 + 0];
      const y1 = bottomLoop3[i3 + 1];
      const z1 = bottomLoop3[i3 + 2];

      const x2 = topLoop3[i3 + 0];
      const y2 = topLoop3[i3 + 1];
      const z2 = topLoop3[i3 + 2];

      const x3 = topLoop3[i3 + 3];
      const y3 = topLoop3[i3 + 4];
      const z3 = topLoop3[i3 + 5];

      if (z2 > z1) {
        const perimeterFace = faceByFourPoints(x0, y0, z0, x1, y1, z1, x2, y2, z2, x3, y3, z3);
        if (perimeterFace) {
          perimeterFaces.push(perimeterFace);
        }
      } else if (z3 > z0) {
        const perimeterFace = faceByFourPoints(x2, y2, z2, x3, y3, z3, x0, y0, z0, x1, y1, z1);
        if (perimeterFace) {
          perimeterFaces.push(perimeterFace);
        }
      }
    }

    clipFaces({
      faces: perimeterFaces,
      options: {
        clippingNodes: [clippingNodeNew(discardBSP)],
        epsilon: MULTIGABLE_BSP_EPSILON,
        coplanarsToFront: true,
      },
      clippedFaces: roofVolumeFaces,
      offsetZ: ROOF_CLIP_CONTEXT_TOP_OFFSET,
    });

    roofVolumeFaces.push(faceNew([topLoop], planeCopy(plane)));
    roofVolumeFaces.push(
      faceNew(
        [polyLine2SetCW(planeProjectPolyLine3(bottomPlaneReversed, bottomLoop3))],
        planeCopy(bottomPlaneReversed),
      ),
    );
  }

  return roofVolumeFaces;
}

function buildRoofVolumeBSP(building: Building): NodeBSP | null {
  const { roofSystems: roofs, rooms } = building;

  const roofVolumeFaces: Face[] = [];

  const addWallFaces = ({ leftFaces }: RoofWallFaces) => {
    roofVolumeFaces.push(...leftFaces);
  };

  const addTrimFaces = ({ faces }: FaceArrayViewEntity) => {
    roofVolumeFaces.push(...faces);
  };

  const addTrimsFaces = (trims: FaceArrayViewEntity[]) => {
    for (const trim of trims) {
      addTrimFaces(trim);
    }
  };

  // iterate roofs
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs, clipContext } = roof;

    // iterate sub-roofs
    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];
      const {
        wallFaces,
        trimsRoofMat,
        friezeBoards,
        fasciaBoards,
        fasciaTopTrims,
        soffits,
        bellyBandBoards,
        wallBaseBoards,
      } = subRoof;

      // build roof volume faces
      buildRoofVolumeFaces(subRoof, clipContext, roofVolumeFaces);

      // add roof walls
      const { startWallFaces, endWallFaces } = wallFaces;

      addWallFaces(startWallFaces);
      addWallFaces(endWallFaces);

      // add trims
      addTrimsFaces(trimsRoofMat);
      addTrimsFaces(friezeBoards);
      addTrimsFaces(fasciaBoards);
      addTrimsFaces(fasciaTopTrims);
      addTrimsFaces(soffits);
      addTrimsFaces(bellyBandBoards);
      addTrimsFaces(wallBaseBoards);
    }
  }

  // build roof volumes polygons from faces
  const polygons = buildPolygonsFromFaces({
    faces: roofVolumeFaces,
    ignoreFastPath: true,
  });

  // build room volumes polygons
  for (const roomId in rooms) {
    const room = rooms[roomId];

    const volumePolygons = buildRoomVolumePolygons(room);
    polygons.push(...volumePolygons);
    polygons.push(...volumePolygons.map((x) => polygonBSPMoveAlongNormal(polygonBSPFlip(x), 1)));
  }

  // build BSP
  const root = buildBSPNode({ polygons });
  removeCoplanarLinesBSP(root, polygons);

  return root;
}
